<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { useNow } from '@vueuse/core';
  import { Calendar, ChevronLeft, ChevronRight } from 'lucide-vue-next';
  import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui';
  import {
    MIN_YEAR,
    MONTH_NAMES_SHORT,
    formatPeriodLabel,
    getCurrentPeriod,
    getMinPeriod,
    getWeekAnchorMonth,
    getWeeksOfMonth,
    isFuturePeriod,
    isPastPeriod,
    type Granularity,
  } from '../model/period';

  interface Props {
    granularity: Granularity;
    period: string;
    disabled?: boolean;
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{
    (e: 'select', period: string): void;
  }>();

  const YEARS_PER_PAGE = 12;
  const WEEKDAY_LABELS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

  const isOpen = ref(false);
  const viewYear = ref(0);
  const viewMonth = ref(1);
  const now = useNow({ interval: 60_000 });

  const periodLabel = computed(() => formatPeriodLabel(props.granularity, props.period));
  const currentYear = computed(() => Number(getCurrentPeriod('yearly', now.value)));

  function syncViewWithPeriod(): void {
    if (props.granularity === 'weekly') {
      const anchor = getWeekAnchorMonth(props.period);
      viewYear.value = anchor.year;
      viewMonth.value = anchor.month;
      return;
    }
    viewYear.value = Number(props.period.slice(0, 4));
  }

  watch(() => [props.granularity, props.period], syncViewWithPeriod, { immediate: true });

  watch(isOpen, (open) => {
    if (open) syncViewWithPeriod();
  });

  const monthlyViewKey = computed(() => `${viewYear.value}-${String(viewMonth.value).padStart(2, '0')}`);

  const monthOptions = computed(() =>
    MONTH_NAMES_SHORT.map((label, index) => {
      const key = `${viewYear.value}-${String(index + 1).padStart(2, '0')}`;
      return {
        key,
        label,
        disabled: isFuturePeriod('monthly', key, now.value) || isPastPeriod('monthly', key),
      };
    })
  );

  const semesterOptions = computed(() =>
    [1, 2].map((half) => {
      const key = `${viewYear.value}-S${half}`;
      return {
        key,
        label: `${half}º semestre`,
        disabled: isFuturePeriod('semiannual', key, now.value) || isPastPeriod('semiannual', key),
      };
    })
  );

  const yearPageStart = computed(() => Math.floor(viewYear.value / YEARS_PER_PAGE) * YEARS_PER_PAGE);

  const yearOptions = computed(() =>
    Array.from({ length: YEARS_PER_PAGE }, (_, index) => {
      const key = String(yearPageStart.value + index);
      return {
        key,
        label: key,
        disabled: isFuturePeriod('yearly', key, now.value) || isPastPeriod('yearly', key),
      };
    })
  );

  const weekRows = computed(() =>
    getWeeksOfMonth(viewYear.value, viewMonth.value).map((week) => ({
      ...week,
      disabled: isFuturePeriod('weekly', week.key, now.value) || isPastPeriod('weekly', week.key),
    }))
  );

  const viewLabel = computed(() => {
    switch (props.granularity) {
      case 'weekly':
        return formatPeriodLabel('monthly', monthlyViewKey.value);
      case 'yearly':
        return `${yearPageStart.value} – ${yearPageStart.value + YEARS_PER_PAGE - 1}`;
      default:
        return String(viewYear.value);
    }
  });

  const isNextViewDisabled = computed(() => {
    switch (props.granularity) {
      case 'weekly':
        return monthlyViewKey.value >= getCurrentPeriod('monthly', now.value);
      case 'yearly':
        return yearPageStart.value + YEARS_PER_PAGE > currentYear.value;
      default:
        return viewYear.value >= currentYear.value;
    }
  });

  const isPreviousViewDisabled = computed(() => {
    switch (props.granularity) {
      case 'weekly':
        return monthlyViewKey.value <= getMinPeriod('monthly');
      case 'yearly':
        return yearPageStart.value <= Math.floor(MIN_YEAR / YEARS_PER_PAGE) * YEARS_PER_PAGE;
      default:
        return viewYear.value <= MIN_YEAR;
    }
  });

  function shiftView(offset: number): void {
    if (props.granularity === 'weekly') {
      const monthIndex = viewYear.value * 12 + (viewMonth.value - 1) + offset;
      viewYear.value = Math.floor(monthIndex / 12);
      viewMonth.value = (monthIndex % 12) + 1;
      return;
    }
    viewYear.value += props.granularity === 'yearly' ? offset * YEARS_PER_PAGE : offset;
  }

  function goToPreviousView(): void {
    if (isPreviousViewDisabled.value) return;
    shiftView(-1);
  }

  function goToNextView(): void {
    if (isNextViewDisabled.value) return;
    shiftView(1);
  }

  function select(key: string, disabled: boolean): void {
    if (disabled || props.disabled) return;
    emit('select', key);
    isOpen.value = false;
  }

  watch(
    () => props.disabled,
    (disabled) => {
      if (disabled) isOpen.value = false;
    }
  );

  defineExpose({ viewYear, viewMonth });

  const optionClass = (option: { key: string; disabled: boolean }) => [
    'h-9 rounded-lg text-sm',
    option.key === props.period
      ? 'bg-[color-mix(in_srgb,var(--color-white-01),var(--theme-secondary))] font-medium text-white'
      : 'text-gray-07',
    option.disabled ? 'cursor-not-allowed text-gray-03 opacity-50' : 'cursor-pointer hover:bg-gray-09',
  ];
</script>

<template>
  <Popover v-model:open="isOpen">
    <PopoverTrigger as-child>
      <button
        type="button"
        aria-label="Selecionar período"
        data-testid="period-picker-trigger"
        :disabled="props.disabled"
        class="flex items-center gap-2 text-gray-07"
        :class="props.disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'"
      >
        <Calendar class="size-5" />
        <span class="text-sm font-medium">{{ periodLabel }}</span>
      </button>
    </PopoverTrigger>
    <PopoverContent
      align="start"
      data-testid="period-picker-content"
      class="w-72 rounded-lg border-gray-02 bg-white-01 p-4 shadow-lg"
    >
      <div class="flex items-center justify-between">
        <button
          type="button"
          aria-label="Anterior"
          :disabled="isPreviousViewDisabled"
          class="rounded-lg p-1 text-gray-07"
          :class="
            isPreviousViewDisabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer hover:bg-gray-09'
          "
          @click="goToPreviousView"
        >
          <ChevronLeft class="size-5" />
        </button>
        <span
          data-testid="period-picker-view-label"
          class="text-sm font-medium text-gray-07"
        >
          {{ viewLabel }}
        </span>
        <button
          type="button"
          aria-label="Próximo"
          :disabled="isNextViewDisabled"
          class="rounded-lg p-1 text-gray-07"
          :class="isNextViewDisabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer hover:bg-gray-09'"
          @click="goToNextView"
        >
          <ChevronRight class="size-5" />
        </button>
      </div>
      <div
        v-if="granularity === 'monthly'"
        class="mt-4 grid grid-cols-3 gap-2"
      >
        <button
          v-for="option in monthOptions"
          :key="option.key"
          type="button"
          data-testid="period-picker-option"
          :disabled="option.disabled"
          :class="optionClass(option)"
          @click="select(option.key, option.disabled)"
        >
          {{ option.label }}
        </button>
      </div>
      <div
        v-else-if="granularity === 'yearly'"
        class="mt-4 grid grid-cols-3 gap-2"
      >
        <button
          v-for="option in yearOptions"
          :key="option.key"
          type="button"
          data-testid="period-picker-option"
          :disabled="option.disabled"
          :class="optionClass(option)"
          @click="select(option.key, option.disabled)"
        >
          {{ option.label }}
        </button>
      </div>
      <div
        v-else-if="granularity === 'semiannual'"
        class="mt-4 flex flex-col gap-2"
      >
        <button
          v-for="option in semesterOptions"
          :key="option.key"
          type="button"
          data-testid="period-picker-option"
          :disabled="option.disabled"
          :class="optionClass(option)"
          @click="select(option.key, option.disabled)"
        >
          {{ option.label }}
        </button>
      </div>
      <div
        v-else
        class="mt-4"
      >
        <div class="grid grid-cols-7 gap-1">
          <span
            v-for="label in WEEKDAY_LABELS"
            :key="label"
            class="flex h-8 items-center justify-center text-xs font-medium text-gray-03"
          >
            {{ label }}
          </span>
        </div>
        <div class="flex flex-col gap-1">
          <button
            v-for="week in weekRows"
            :key="week.key"
            type="button"
            :disabled="week.disabled"
            data-testid="period-picker-week"
            class="grid grid-cols-7 gap-1 rounded-lg"
            :class="[
              week.key === props.period
                ? 'bg-[color-mix(in_srgb,var(--color-white-01),var(--theme-secondary))] font-medium text-white'
                : 'text-gray-07',
              week.disabled ? 'cursor-not-allowed text-gray-03 opacity-50' : 'cursor-pointer hover:bg-gray-09',
            ]"
            @click="select(week.key, week.disabled)"
          >
            <span
              v-for="(day, dayIndex) in week.days"
              :key="dayIndex"
              class="flex h-8 items-center justify-center text-sm"
              :class="!day.inMonth && week.key !== props.period ? 'text-gray-02' : ''"
            >
              {{ day.day }}
            </span>
          </button>
        </div>
      </div>
    </PopoverContent>
  </Popover>
</template>
